import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ExpandableTable, Icons } from '@hmsexp/component-library';
import { StyleSheet } from 'react-native';
import { renderCell } from './RenderCell';
import Pagination from '../Common/Pagination';
import { View } from '../Common/react-native-components';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import GenericNoData from '../Common/NoData/GenricNoData';

const ExpandableGrid = ({
  columns,
  pageSize = 1,
  fetchData,
  getNestedData,
  widgetSpecificKeys,
  dataCache,
  loading,
  metricKey = 'default',
  showPagination = false,
  componentMap = {},
  style,
  hasNestedData = true
}) => {
  const [hoveredRowId, setHoveredRowId] = useState();
  const [tableDataLoading, setTableDataLoading] = useState(loading);
  const [fetchedRows, setFetchedRows] = useState(new Set());
  const [noData, setNoData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRowToFetch, setExpandedRowToFetch] = useState();
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null
  });

  const getCacheKey = (page, field, direction) => {
    return `${field || 'none'}-${direction || 'none'}-${page}`;
  };
  const [paginatedData, setPaginatedData] = useState();

  const dispatch = useDispatch();

  const insertSubData = (fullData, dataToInsert, id) => {
    return fullData.map(d => {
      if (d.id === id) {
        return {
          ...d,
          subData: dataToInsert
        };
      }
      if (d.subData && Array.isArray(d.subData)) {
        const newSub = insertSubData(d.subData, dataToInsert, id);
        if (newSub !== d.subData) {
          return { ...d, subData: newSub };
        }
      }
      return d;
    });
  };

  useEffect(() => {
    const loadData = async () => {
      const cacheKey = getCacheKey(currentPage, sortConfig.field, sortConfig.direction);

      const cached = get(dataCache, `${metricKey}.${cacheKey}`);
      if (cached || currentPage == 1) return;

      setTableDataLoading(true);

      try {
        const response = await fetchData({
          widget_config: {
            ...widgetSpecificKeys,
            offset: pageSize * (currentPage - 1),
            limit: pageSize
          }
        });

        dispatch({
          type: 'SAVE_WIDGET_TABLE_DATA',
          payload: {
            ...widgetSpecificKeys,
            metricType: metricKey,
            cacheKey,
            tableData: response.tableData,
            total: response.total
          }
        });

        setTotalRecords(response.total);
      } finally {
        setTableDataLoading(false);
      }
    };

    loadData();
  }, [currentPage]);

  useEffect(() => {
    const cacheKey = getCacheKey(currentPage, sortConfig.field, sortConfig.direction);
    const cached = get(dataCache, `${metricKey}.${cacheKey}.data`);
    const totalRecords = get(dataCache, `${metricKey}.totalRecords`);
    if (cached?.length === 0) {
      setNoData(true);
    }

    if (cached) {
      setTotalRecords(totalRecords);
      setPaginatedData(cached);
    }
  }, [dataCache, metricKey, currentPage]);

  useEffect(() => {
    if (!expandedRowToFetch) return;

    const fetchNestedData = async () => {
      setTableDataLoading(true);

      try {
        const nested = await getNestedData(expandedRowToFetch);
        const cacheKey = getCacheKey(currentPage, sortConfig.field, sortConfig.direction);
        setFetchedRows(prev => new Set(prev).add(expandedRowToFetch.id));
        const newData = insertSubData(
          paginatedData,
          typeof nested === Array ? nested : nested?.tableData,
          expandedRowToFetch.id
        );
        dispatch({
          type: 'SAVE_WIDGET_TABLE_DATA',
          payload: {
            ...widgetSpecificKeys,
            tableData: newData,
            cacheKey,
            metricType: metricKey
          }
        });
      } catch (error) {
        console.error('Error fetching nested data:', error);
      } finally {
        setTableDataLoading(false);
        setExpandedRowToFetch(null);
      }
    };

    fetchNestedData();
  }, [expandedRowToFetch]);

  const handleRowExpand = useCallback(
    row => {
      if (!fetchedRows.has(row.id)) {
        setExpandedRowToFetch(row);
      }
    },
    [fetchedRows]
  );

  const renderCollapsibleIcon = useCallback(({ collapsed }) => {
    return collapsed ? <Icons.ChevronRight size='L' /> : <Icons.ChevronDown size='L' />;
  }, []);

  const handlePageChange = page => {
    setCurrentPage(page);
    // loadData();
  };

  const handleSort = (columnKey, direction) => {
    setSortConfig({ field: columnKey, direction });
    setCurrentPage(1);
    // loadData();
  };

  const patchColumns = (cols, hoveredRowId, setHoveredRowId) => {
    return cols.map(col => {
      const patched = {
        ...col,
        ...(col.field && {
          Cell: props =>
            renderCell({
              hoveredRowId,
              setHoveredRowId,
              componentMap,
              handleSort,
              sortConfig,
              ...props
            })
        })
      };

      if (col.columns && Array.isArray(col.columns)) {
        patched.columns = patchColumns(col.columns, hoveredRowId, setHoveredRowId);
      }
      return patched;
    });
  };
  const patchedColumns = useMemo(
    () => patchColumns(get(dataCache, [metricKey, 'columns'], columns), hoveredRowId, setHoveredRowId),
    [columns, dataCache, metricKey, hoveredRowId, sortConfig]
  );

  return (
    <View style={[styles.container, tableDataLoading && styles.containerLoading]}>
      {tableDataLoading && <View style={styles.loadingOverlay}></View>}
      {noData && <GenericNoData />}

      {!noData && (
        <ExpandableTable
          columns={patchedColumns}
          collapsibleIconsDirection='left'
          applyRenderRowToNestedRows={true}
          renderCollapsibleIcon={renderCollapsibleIcon}
          data={paginatedData}
          style={{ ...tableStyles, ...style }}
          getCellProps={({ column, value, row }) => {
            const isHovered = hoveredRowId === row.id;
            return {
              containerStyle: styles.cellContainer
            };
          }}
          hasNestedData={hasNestedData}
          showTitleBar={false}
          customOnPressIcon={e => {
            handleRowExpand(e);
          }}
        />
      )}
      {showPagination && !noData && !tableDataLoading && (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 3,
    display: 'flex',
    opacity: 1,
    pointerEvents: 'auto'
  },
  containerLoading: {
    opacity: 0.4,
    pointerEvents: 'none'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999
  },
  cellContainer: {
    backgroundColor: '#fff'
  }
});

const tableStyles = {
  container: {
    borderWidth: 0
  },
  containerStyle: {
    backgroundColor: 'white'
  },
  headerCellWrapper: {
    backgroundColor: '#fff'
  },
  headerContainer: {
    height: 60,
    borderWidth: 0,
    backgroundColor: '#fff'
  },
  headerTextWrapperStyle: {
    backgroundColor: '#fff',
    fontWeight: '800'
  }
};

export default ExpandableGrid;
